import { sortBy, includes } from 'lodash'

import {
  getAttributeNames,
  getCSV,
  getDepartmentNames,
  getHiringDataNames,
  getProjectNames,
  getPermissionNames,
  getPositionName,
  getPositionNames,
  getTagNames,
  getTagName,
  getTeamNames,
  hasAccessToThis
} from 'services/Utilities'
import { store } from 'store/reducers'
import { t } from 'translations'
import { Format } from 'services/Dates'

export const exportUsersAsCSV = async users => {
  var fields = []
  const {
    resources: { teams },
    settings
  } = store.getState()

  // Permissions
  const userSeePersonalInfo = hasAccessToThis('userSeePersonalInfo')
  const userSeeHiringInfo = hasAccessToThis('userSeeHiringInfo')
  const userSeeContactInfo = hasAccessToThis('userSeeContactInfo')
  const userSeeConnectionInfo = hasAccessToThis('userSeeConnectionInfo')
  const canSeeHiringdata = hasAccessToThis('hiringDataSee')
  const canSeeProjects = hasAccessToThis('projectSee')
  const canManageSpecificRestrictions = hasAccessToThis('userManageSpecificRestrictions')
  const canSeeAttributes = hasAccessToThis('attributeSee')

  // Settings
  const regionalTagsEnabled = settings.regionalTags
  const enableAttributes = settings.enableAttributes
  const occupationsEnabled = settings.enableOccupations
  const projectRestrictionsEnabled = settings.projectRestrictions
  const enablePreferredPronouns = settings.enablePreferredPronouns
  const enableLegalName = settings.enableLegalName

  const data = users.map(employee => {
    var employeeParsed = {}

    // Default fields
    employeeParsed.fullName = employee.fullName

    // Set the personnal information's field if the user has access
    if (userSeePersonalInfo) {
      if (enablePreferredPronouns)
        employeeParsed.preferredPronouns = t(`manager.users.pronouns.${employee.preferredPronouns || 'OTHER'}`, {
          preferredPronounOther: employee.preferredPronounOther || 'N/A'
        })
      if (enableLegalName) {
        employeeParsed.legalName = employee.legalName || 'N/A'
      }
      if (employee.birthDate) employeeParsed.birthDate = employee.birthDate
      employeeParsed.language = employee.language
    }

    // Set the hiring information's field if the user has access
    if (userSeeHiringInfo) {
      employeeParsed.employeeNumber = employee.employeeNumber
      employeeParsed.archived = employee.archived ? t(`TRUE`) : t(`FALSE`)
      employeeParsed.billableThisMonth = employee.billableThisMonth ? t(`TRUE`) : t(`FALSE`)
      employeeParsed.employeeID = employee.employeeID
      employeeParsed.seniorityDate = employee.seniorityDate
      if (employee.occupations && employee.occupations.primary && occupationsEnabled)
        employeeParsed.primaryOccupation = getPositionName(employee.occupations.primary)
      if (employee.occupations && employee.occupations.secondary && occupationsEnabled)
        employeeParsed.secondaryOccupation = getPositionName(employee.occupations.secondary)
      if (employee.hiringDate) employeeParsed.hiringDate = Format(employee.hiringDate, { inFormat: 'date', outFormat: 'date' })
      employeeParsed.priorityNumber = employee.priorityNumber
      if (employee.departments) employeeParsed.departments = getDepartmentNames(employee.departments)
      if (employee.groups) employeeParsed.groups = getPermissionNames(employee.groups)
      if (employee.hiringData && canSeeHiringdata) {
        // Filter hiringData for the different types and then map to return the name and dates
        employeeParsed.hiringDataPermit = getHiringDataNames(
          employee.hiringData.filter(data => data.type === 'permitType'),
          { withDates: true }
        )
        employeeParsed.hiringDataContract = getHiringDataNames(
          employee.hiringData.filter(data => data.type === 'contractType'),
          { withDates: true }
        )
        employeeParsed.hiringDataEmployee = getHiringDataNames(
          employee.hiringData.filter(data => data.type === 'employeeType'),
          { withDates: true }
        )
      }
      if (employee.positions) employeeParsed.positions = getPositionNames(employee.positions)
      // Set the client seniorities field
      employeeParsed.clientSeniorities = sortBy(employee.clientSeniorities, ['seniorityDate'])
        .map(seniority => `${getTagName(seniority.clientId)} (${seniority.seniorityDate})`)
        .join(' - ')

      // Set the position seniorities field
      employeeParsed.positionSeniorities = sortBy(employee.positionSeniorities, ['seniorityDate'])
        .map(seniority => `${getPositionName(seniority.positionId)} (${seniority.seniorityDate})`)
        .join('  - ')
    }

    // Export specific restrictions
    if (canManageSpecificRestrictions && employee.specificRestrictions) employeeParsed.specificRestrictions = employee.specificRestrictions.join(' - ')

    // Set the contact information's field if the user has access
    if (userSeeContactInfo) {
      employeeParsed.personalEmails = employee.emails
        .filter(e => e.type === 'PERSONEL') // Sorry about this typo, but it's encrusted in the codebase.
        .map(email => email.address)
        .join(' - ')
      employeeParsed.workEmails = employee.emails
        .filter(e => e.type === 'WORK')
        .map(email => email.address)
        .join(' - ')
      employeeParsed.phoneNumbers = employee.phoneNumbers.map(phone => `(${phone.phoneNumber} ${t(phone.type)})`).join(' - ')
    }

    // Set the connection information's field if the user has access
    if (userSeeConnectionInfo) employeeParsed.username = employee.username

    // Set the teams field if the user has access
    employeeParsed.teams = getTeamNames(employee.teams)
    employeeParsed.reportsTo = getTeamNames(teams.filter(team => includes(team.reportsTo, employee._id)))

    // Set Project Restrictions fields (Locked, Preferred, and Blocked) if the user has access
    employeeParsed.projects = getProjectNames(employee.projects)
    if (canSeeProjects && projectRestrictionsEnabled) {
      employeeParsed.lockedTo = getProjectNames(employee.projectRestrictions?.locked, { canBeNull: true, noValue: '' })
      employeeParsed.preferredOn = getProjectNames(employee.projectRestrictions?.preferred, { canBeNull: true, noValue: '' })
      employeeParsed.blockedFrom = getProjectNames(employee.projectRestrictions?.blocked, { canBeNull: true, noValue: '' })
    }
    // Add regions if the setting is active
    if (regionalTagsEnabled && employee.regionalTags) {
      employeeParsed.regionalTags = getTagNames(employee.regionalTags)
    }

    // Set the attributes field if the user has access
    if (canSeeAttributes && enableAttributes) {
      employeeParsed.attributes = getAttributeNames(employee.attributes, { withRank: true })
    }

    return employeeParsed
  })

  // Default fields
  fields.push({ value: 'fullName', label: t(`manager.users.FULLNAME`) })

  if (userSeePersonalInfo) {
    fields.push(
      { value: 'preferredPronouns', label: t(`manager.users.PREFERREDPRONOUNS`) },
      { value: 'legalName', label: t(`manager.users.LEGALNAME`) },
      { value: 'birthDate', label: t(`manager.users.BIRTH_DATE`) },
      { value: 'language', label: t(`manager.users.LANGUAGE`) }
    )
  }
  if (userSeeConnectionInfo) fields.push({ value: 'username', label: t(`manager.users.USERNAME`) })
  if (userSeeHiringInfo) {
    fields.push(
      { value: 'employeeNumber', label: t(`manager.users.EMPLOYEE_NUMBER`) },
      { value: 'billableThisMonth', label: t(`manager.users.BILLABLE`) },
      { value: 'archived', label: t(`manager.users.ARCHIVED`) },
      { value: 'employeeID', label: t(`manager.users.EMPLOYEE_ID`) },
      { value: 'hiringDate', label: t(`manager.users.HIRING_DATE`) },
      { value: 'seniortyDate', label: t(`manager.users.SENIORITY_DATE`) },
      { value: 'priorityNumber', label: t(`manager.users.PRIORITY_NUMBER`) },
      { value: 'departments', label: t(`manager.users.DEPARTMENTS`) },
      { value: 'groups', label: t(`manager.users.GROUPS`) },
      { value: 'teams', label: t(`manager.users.TEAMS`) },
      { value: 'projects', label: t(`manager.users.PROJECTS`) },
      { value: 'reportsTo', label: t(`manager.users.REPORTS_TO`) },
      { value: 'hiringDataPermit', label: t(`manager.users.PERMIT_TYPE`) },
      { value: 'hiringDataContract', label: t(`manager.users.CONTRACT_TYPE`) },
      { value: 'hiringDataEmployee', label: t(`manager.users.EMPLOYEE_TYPE`) },
      { value: 'clientSeniorities', label: t(`manager.users.CLIENTSENIORITIES`) },
      { value: 'positionSeniorities', label: t(`manager.users.POSITIONSENIORITIES`) }
    )

    if (occupationsEnabled) {
      fields.push(
        { value: 'primaryOccupation', label: t(`manager.users.PRIMARY_OCCUPATION`) },
        { value: 'secondaryOccupation', label: t(`manager.users.SECONDARY_OCCUPATION`) }
      )
    }
    if (canManageSpecificRestrictions) fields.push({ value: 'specificRestrictions', label: t(`manager.users.SPECIFIC_RESTRICTIONS`) })
  }
  if (userSeeContactInfo) {
    fields.push(
      { value: 'personalEmails', label: t(`manager.users.PERSONAL_EMAILS`) },
      { value: 'workEmails', label: t(`manager.users.WORK_EMAILS`) },
      { value: 'phoneNumbers', label: t(`manager.users.PHONES`) }
    )
  }
  if (canSeeAttributes && enableAttributes) fields.push({ value: 'attributes', label: t(`manager.users.ATTRIBUTES`) })
  fields.push({ value: 'positions', label: t(`manager.users.POSITIONS`) })
  if (canSeeProjects) {
    fields.push({ value: 'lockedTo', label: t(`manager.users.PROJECT_LOCKED`) })
    fields.push({ value: 'preferredOn', label: t(`manager.users.PROJECT_PREFERRED`) })
    fields.push({ value: 'blockedFrom', label: t(`manager.users.PROJECT_BLOCKED`) })
  }
  if (regionalTagsEnabled) fields.push({ value: 'regionalTags', label: t(`manager.users.REGIONAL_TAGS`) })

  getCSV(data, fields, `${t('manager.users.REPORT_NAME')}_${Format(new Date(), { outFormat: 'date' })}`)
}
